import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateCompany = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  const [formData, setFormData] = useState({
    companyName: '',
    companyType: '',
    description: '',
    headquartersAddress: {
      street: '',
      state: '',
      city: '',
      zipCode: ''
    },
    email: '',
    website: '',
    phone: '',
    fax: ''
  });

  const initialFormData = {
    companyName: '',
    companyType: '',
    description: '',
    headquartersAddress: {
      street: '',
      state: '',
      city: '',
      zipCode: ''
    },
    email: '',
    website: '',
    phone: '',
    fax: ''
  };

  const hasFormChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData) || logoFile !== null;
  };

  useEffect(() => {
    loadStates();
  }, []);

  // ESC key handler with confirmation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (confirmModal.isOpen) {
          setConfirmModal({ ...confirmModal, isOpen: false });
        } else {
          handleCancelWithConfirmation();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [formData, logoFile, confirmModal]);

  const handleCancelWithConfirmation = () => {
    if (hasFormChanges()) {
      setConfirmModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave? All changes will be lost.',
        type: 'warning',
        confirmText: 'Leave Anyway',
        cancelText: 'Keep Editing',
        onConfirm: () => navigate('/admin/company-settings/list')
      });
    } else {
      navigate('/admin/company-settings/list');
    }
  };

  const loadStates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/states`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadCities = async (stateCode) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/cities/${stateCode}`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleStateChange = (stateCode) => {
    setFormData({
      ...formData,
      headquartersAddress: {
        ...formData.headquartersAddress,
        state: stateCode,
        city: ''
      }
    });
    if (stateCode) {
      loadCities(stateCode);
    } else {
      setCities([]);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.companyName || !formData.companyType) {
      setConfirmModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Company Name and Company Type are required fields.',
        type: 'error',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    setLoading(true);

    try {
      // Clean up data - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        email: formData.email?.trim() || null,
        website: formData.website?.trim() || null,
        phone: formData.phone?.trim() || null,
        fax: formData.fax?.trim() || null,
        description: formData.description?.trim() || null,
        headquartersAddress: formData.headquartersAddress?.street?.trim() ? formData.headquartersAddress : null
      };
      
      // Create company using axios
      const response = await axios.post(`${BACKEND_URL}/api/admin/companies`, cleanedData);
      
      const data = response.data;

      // Upload logo if exists
      if (logoFile && data.companyId) {
        const logoFormData = new FormData();
        logoFormData.append('file', logoFile);

        try {
          await axios.post(`${BACKEND_URL}/api/admin/companies/${data.companyId}/logo`, logoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (logoError) {
          console.warn('Logo upload failed, but company was created:', logoError);
        }
      }

      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: 'Company created successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => navigate('/admin/company-settings/list')
      });

      // Reset form
      setFormData({
        companyName: '',
        companyType: '',
        description: '',
        headquartersAddress: {
          street: '',
          state: '',
          city: '',
          zipCode: ''
        },
        email: '',
        website: '',
        phone: '',
        fax: ''
      });
      setLogoFile(null);
      setLogoPreview(null);
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Error creating company:', error);
      
      // Handle validation errors from backend
      let errorMessage = 'Failed to create company. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (422)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.join('.') || 'Field';
            return `${field}: ${err.msg}`;
          }).join('\n');
        } else if (errorData.detail) {
          errorMessage = String(errorData.detail);
        } else if (errorData.message) {
          errorMessage = String(errorData.message);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        type: 'error',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Create New Company</h2>
            <p className="text-gray-600">Add a new company to the system</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl p-8">
            {/* Company Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter company name"
              />
            </div>

            {/* Company Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Type <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.companyType}
                onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select company type</option>
                <option value="General Contractor">General Contractor</option>
                <option value="Sub Contractor">Sub Contractor</option>
              </select>
            </div>

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Description
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description about the company (1-2 paragraphs)"
              />
            </div>

            {/* Headquarters Address */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Headquarters Address</h3>
              
              <div className="space-y-4">
                {/* Street */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.headquartersAddress.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      headquartersAddress: { ...formData.headquartersAddress, street: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main St"
                  />
                </div>

                {/* State and City */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <select
                      value={formData.headquartersAddress.state}
                      onChange={(e) => handleStateChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select state</option>
                      {states.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <select
                      value={formData.headquartersAddress.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        headquartersAddress: { ...formData.headquartersAddress, city: e.target.value }
                      })}
                      disabled={!formData.headquartersAddress.state}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    >
                      <option value="">Select city</option>
                      {cities.map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.headquartersAddress.zipCode}
                    onChange={(e) => setFormData({
                      ...formData,
                      headquartersAddress: { ...formData.headquartersAddress, zipCode: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="contact@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fax Number
                </label>
                <input
                  type="tel"
                  value={formData.fax}
                  onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4568"
                />
              </div>
            </div>

            {/* Logo Upload */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label htmlFor="logo-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload logo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </label>
                  </div>
                </div>
                {logoPreview && (
                  <div className="w-32 h-32 border border-gray-300 rounded-lg overflow-hidden">
                    <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={handleCancelWithConfirmation}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                style={{ background: '#FF6600' }}
              >
                {loading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
          
          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            confirmText={confirmModal.confirmText}
            cancelText={confirmModal.cancelText}
            showCancel={confirmModal.showCancel}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateCompany;
